/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { CanvasAngularLand } from '../../../app-data/interfaces/canvas-angular-land';
import { AbstractApplication, ICanvasDestroyable } from '@appCanvasCommon/core/AbstractApplication';
import { Subject } from 'rxjs';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { takeUntil } from 'rxjs/operators';
import {
	IPrintConfiguration,
	IPrintStatus,
	PaperOrientations,
	PrintActions,
	PrintTextSizes,
} from '../../../app-data/constants/plan-print.model';
import { Lightbox } from '@appCanvasCommon/export/Lightbox';
import { LightboxMask } from '@appCanvasCommon/export/LightboxMask';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { Camera } from '@appCanvasCommon/core/Camera';
import { Point, Rectangle } from 'pixi.js-legacy';
import { cssVar } from '@touchplan/css-var';
import { ExportCapture, ICapturedTile } from '@appCanvasCommon/export/ExportCapture';
import { createExportContext } from '@appCanvasCommon/export/ExportHelpers';
import { IExportContext } from '@appCanvasCommon/export/IExportContext';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { HideTimeline } from '@appPlanApplication/export/components/HideTimeline';
import { PaperUnits } from '../../../app-data/constants/paper-sizes';
import { HideTodayLine } from '@appPlanApplication/export/components/HideTodayLine';
import { HidePromisePeriod } from '@appPlanApplication/export/components/HidePromisePeriod';
import { DrawWhiteBackground } from '@appPlanApplication/export/components/DrawWhiteBackground';
import { HandleTicketInteraction } from '@appPlanApplication/export/components/HandleTicketInteraction';
import { HideSwimlaneLabel } from '@appPlanApplication/export/components/HideSwimlaneLabel';
import { TicketPlanApplication } from '@appPlanApplication/TicketPlanApplication';
import { TextSizeController } from '@appPlanApplication/export/TextSizeController';
import { HideMilestoneLabel } from '@appPlanApplication/export/components/HideMilestoneLabel';
import { HideActiveLineLabel } from '@appPlanApplication/export/components/HideActiveLineLabel';
import { ProjectAndPlanLabelDrawer } from '@appPlanApplication/export/drawers/ProjectAndPlanLabelDrawer';
import { RoleLegendDrawer } from '@appPlanApplication/export/drawers/RoleLegendDrawer';
import { CrewSizeCapturer } from '@appPlanApplication/export/components/CrewSizeCapturer';
import { CrewSizeDrawer } from '@appPlanApplication/export/drawers/CrewSizeDrawer';
import { IStitchCombinerConfig, TileCombiner } from '@appCanvasCommon/export/TileCombiner';

/**
 * New interface that contains the full canvas dimensions of the export
 */
export interface IExportRect {
	/**
	 * The X value in this object represents the left margin affecting the export.
	 * Since our origin point is the top-left corner of our export image (and not the canvas itself)
	 * we can subtract by this number to get to the edge of our canvas element.
	 */
	x: number;
	/**
	 * The Y value in this object represents the top margin affecting the export.
	 * Since our origin point is the top-left corner of our export image (and not the canvas itself)
	 * we can subtract by this number to get to the edge of our canvas element.
	 */
	y: number;
	/**
	 * The width of our export image. This represents the stitched image
	 */
	width: number;
	/**
	 * The height of our export image. This represents the stitched image
	 */
	height: number;
	/**
	 * This is the full width of our stitched canvas image, including any margins
	 */
	widthWithMargin: number;
	/**
	 * This is the full height of our stitched canvas image, including any margins
	 */
	heightWithMargin: number;
}

/**
 * Configuration settings for the print preview lightbox
 */
const settings = {
	maxScreenPercentage: 0.8,
	minSize: 100,
	margin: 4,
	leftOffset: cssVar.sideMenuWidth_px + cssVar.boxSize350_px,
	pixelsPerInch: 50,
	lightboxAlphs: 0.5,
	lightboxColor: 0x9b9b9b,
	stitchMargin: 100, // Margin to apply for the final stitched image
	combinerThreshold: 150, // Compress tiles when there are over this amount of tiles captured
	maxOutputStitches: 150, // Maximum amount of tiles to compress down to
};

/**
 * FancyPrint manages the print preview and capture functionality for plan exports.
 * It displays a lightbox preview of the print area and captures the plan content in tiles,
 * which are then emitted to the print service so that subscribers can further process
 * the captured and compressed tiles.
 */
export class FancyPrint implements ICanvasDestroyable {
	/**
	 * Makes observers stop...observing
	 */
	private _shutdownSubject$: Subject<void> = new Subject<void>();
	/**
	 * Stores the last print configuration received from the print service
	 */
	private _lastConfig!: IPrintConfiguration | null;
	/**
	 * Visual overlay that darkens the screen outside the print preview area
	 */
	private _lightbox: Lightbox = new Lightbox();
	/**
	 * Mask that creates the "cutout" effect in the lightbox to show the print preview area
	 */
	private _lightboxMask: LightboxMask = new LightboxMask();
	/**
	 * Rectangle defining the print preview area dimensions and position
	 */
	private _viewport: Rectangle = new Rectangle();
	/**
	 * Manages the capture and storage of individual tiles during export
	 */
	private _exportCapture: ExportCapture | null;
	/**
	 * Manages export components that modify behavior during the capture process
	 */
	private _components: ComponentManager | null = new ComponentManager(this);
	/**
	 * Instance of our text size controller. Manages text size UI setting
	 * @private
	 */
	private _textSizeController!: TextSizeController | null;
	/**
	 * Public getter for the viewport rectangle
	 * @returns The current print preview viewport dimensions and position
	 */
	get viewport(): Rectangle {
		return this._viewport;
	}
	/**
	 * Constructor initializes the print preview system
	 * @param _canvasAngularLand Angular services
	 * @param _application The AbstractApplication that controls this thing
	 */
	constructor(
		private _canvasAngularLand: CanvasAngularLand | null,
		private _application: AbstractApplication | null
	) {
		this._exportCapture = new ExportCapture(this._application as AbstractApplication);
		this._textSizeController = new TextSizeController(this._canvasAngularLand);
		this.setupLayers();
		this.setupSubscribers();
		this.setupComponents();
	}
	/**
	 * Starts the capture process for the current print configuration.
	 */
	public capture(): void {
		if (!this._lastConfig || !this._application) {
			return;
		}

		const camera: Camera = this._application.camera;
		const originalScale: Point = new Point(camera.scale.x, camera.scale.y);
		const originalPosition: Point = new Point(camera.view.x, camera.view.y);

		try {
			this.hide();
			this.performCapture(this._lastConfig);
			this.show();
		} finally {
			this.dispatchCompleted();
			camera.setScale(originalScale.x, originalScale.y);
			camera.setPosition(originalPosition.x, originalPosition.y);
			this._application.update();
			this._application.markAsDirty();
		}
	}

	/**
	 * Helper function to dispatch when the export is completed. This will dispatch when:
	 * 	- The export completed successfully
	 * 	- The export failed
	 * 	- The export was cancelled/print preview was closed.
	 * @private
	 */
	private dispatchCompleted(): void {
		this._components!.dispatchOnExportComplete(
			this._canvasAngularLand as CanvasAngularLand,
			this._application as AbstractApplication
		);
	}

	/**
	 * Helper function to dispatch when the print preview is opened for the first time.
	 * This is used to update components that should run during the preview phase, and not necessary during the
	 * capture or stitching phases of the process.
	 * @private
	 */
	private dispatchPreviewStarted(): void {
		this._components!.dispatchOnExportPreviewStarted(
			this._canvasAngularLand as CanvasAngularLand,
			this._application as AbstractApplication
		);
	}

	/**
	 * Helper function to dispatch when the print preview is closed.
	 * @private
	 */
	private dispatchPreviewClosed(): void {
		this._components!.dispatchOnExportPreviewClosed(
			this._canvasAngularLand as CanvasAngularLand,
			this._application as AbstractApplication
		);
	}

	/**
	 * Calculates the scale factor needed to capture the viewport content at the target print resolution
	 * @param config The print configuration containing paper size, DPI, and orientation
	 * @returns The scale factor to apply during capture
	 */
	private calculateCaptureScale(config: IPrintConfiguration): number {
		let paperWidth: number = config.width;
		let paperHeight: number = config.height;
		if (config.orientation === PaperOrientations.Landscape) {
			paperWidth = config.height;
			paperHeight = config.width;
		}
		const dpi: number = config.units === PaperUnits.Inches ? config.dpi : 1;
		const finalWidth: number = Math.round(paperWidth * dpi);
		const finalHeight: number = Math.round(paperHeight * dpi);
		const scaleX: number = finalWidth / this._viewport.width;
		const scaleY: number = finalHeight / this._viewport.height;
		const targetScale: number = Math.min(scaleX, scaleY);
		return targetScale;
	}
	/**
	 * Performs the actual tile-based capture process by moving the camera through a grid pattern
	 * @param config The print configuration to use for capture
	 */
	private performCapture(config: IPrintConfiguration): void {
		if (!this._application) return;

		const camera: Camera = this._application.camera;

		this._exportCapture!.purge();

		try {
			this._components!.dispatchOnExportStarted(
				this._canvasAngularLand as CanvasAngularLand,
				this._application
			);

			const currentScale: Point = camera.getScale();
			const captureScale: number = this.calculateCaptureScale(config);
			const newCameraScale: number = currentScale.x * captureScale;

			const lightboxWorldX: number = camera.screenToRealX(this._viewport.x);
			const lightboxWorldY: number = camera.screenToRealY(this._viewport.y);

			const lightboxWorldWidth: number = this._viewport.width / currentScale.x;
			const lightboxWorldHeight: number = this._viewport.height / currentScale.y;

			camera.setScale(newCameraScale, newCameraScale);

			const scaledLightboxWidth: number = lightboxWorldWidth * newCameraScale;
			const scaledLightboxHeight: number = lightboxWorldHeight * newCameraScale;

			const maxTileWidth: number = camera.view.width;
			const maxTileHeight: number = camera.view.height;

			const tilesX: number = Math.ceil(scaledLightboxWidth / maxTileWidth);
			const tilesY: number = Math.ceil(scaledLightboxHeight / maxTileHeight);
			const tileWidth: number = Math.ceil(scaledLightboxWidth / tilesX);
			const tileHeight: number = Math.ceil(scaledLightboxHeight / tilesY);
			const tileWorldWidth: number = tileWidth / newCameraScale;
			const tileWorldHeight: number = tileHeight / newCameraScale;

			for (let tileY = 0; tileY < tilesY; tileY++) {
				for (let tileX = 0; tileX < tilesX; tileX++) {
					// Use Math.ceil to make sure that the tile coordinates are never floating point values.
					// If lightboxWorldX/Y or tileWorldWidth/Height are not integers then the tile coordinates can end up being
					// floating point values like 2202.0000000000005 due to JavaScript's floating-point precision.
					const tileWorldX: number = Math.ceil(lightboxWorldX + tileX * tileWorldWidth);
					const tileWorldY: number = Math.ceil(lightboxWorldY + tileY * tileWorldHeight);
					const captureRect: Rectangle = new Rectangle(
						Math.ceil(tileWorldX * newCameraScale),
						Math.ceil(tileWorldY * newCameraScale),
						tileWidth,
						tileHeight
					);

					const exportContext: IExportContext = createExportContext(
						tileX,
						tileY,
						tilesX,
						tilesY,
						tileWorldX,
						tileWorldY,
						tileWidth,
						tileHeight,
						this._lightbox
					);

					camera.setPosition(-tileWorldX * newCameraScale, -tileWorldY * newCameraScale);
					const app: TicketPlanApplication = this._application as TicketPlanApplication;
					this._application.update();
					(this._application as TicketPlanApplication)?.swimlanes?.forceUpdate();
					this._application.draw();

					this._components!.dispatchOnExportTile(
						this._canvasAngularLand as CanvasAngularLand,
						this._application,
						exportContext
					);

					this._exportCapture!.capture(captureRect);
				}
			}

			const rawTiles = this.generateRawImage(config);
			this._canvasAngularLand!.planPrintService.emitCapturedRawTiles(rawTiles);
			this._canvasAngularLand!.planPrintService.setStatusToCompleted();
		} catch (error) {
			this._canvasAngularLand!.planPrintService.setStatusToFailed('Capture failed');
		} finally {
			this._exportCapture!.purge();
		}
	}

	/**
	 * Generates the raw image tiles for export, including plan tiles and any additional overlay tiles.
	 */
	private generateRawImage(config: IPrintConfiguration): ICapturedTile[] {
		if (!this._exportCapture) return [];

		const exportRect = this.calculateExportRect(config);

		// any additional tiles from components that need to be drawn on top of the plan tiles: role legend, project/plan title, etc
		const additionalTiles = this._components!.dispatchOnExportDrawNode(
			this._canvasAngularLand as CanvasAngularLand,
			this._application as AbstractApplication,
			this._lightbox,
			config,
			exportRect
		);
		// Round all tile positions to prevent floating point values to sneak in.
		// These additional tiles should always have whole pixel values, but currently floating points sneak in.
		const roundedAdditionalTiles: ICapturedTile[] = additionalTiles.map((tile) => ({
			...tile,
			position: {
				x: Math.round(tile.position.x),
				y: Math.round(tile.position.y),
				width: Math.round(tile.position.width),
				height: Math.round(tile.position.height),
			},
		}));

		// plan tiles - do partial stitch if over threshold
		let planTiles: ICapturedTile[] = this._exportCapture.tiles;
		if (planTiles.length > settings.combinerThreshold) {
			planTiles = this.compressTiles(planTiles);
		}

		return [...planTiles, ...roundedAdditionalTiles];
	}

	/**
	 * Calculates the export rectangle dimensions based on the print configuration, margins included
	 * @returns The calculated export rectangle
	 */
	public calculateExportRect(config: IPrintConfiguration): IExportRect {
		let paperWidth: number = config.width;
		let paperHeight: number = config.height;

		if (config.orientation === PaperOrientations.Landscape) {
			paperWidth = config.height;
			paperHeight = config.width;
		}

		const targetDpi: number = config.units === PaperUnits.Inches ? config.dpi : 1;

		const finalWidth: number = Math.round(paperWidth * targetDpi);
		const finalHeight: number = Math.round(paperHeight * targetDpi);
		return {
			x: settings.stitchMargin,
			y: settings.stitchMargin,
			width: finalWidth,
			height: finalHeight,
			widthWithMargin: finalWidth + settings.stitchMargin,
			heightWithMargin: finalHeight + settings.stitchMargin,
		};
	}

	private compressTiles(tiles: ICapturedTile[]): ICapturedTile[] {
		const tileCombiner: TileCombiner = new TileCombiner();
		const partialStitchConfig: IStitchCombinerConfig = {
			maxTilesPerStitch: Math.ceil(tiles.length / settings.maxOutputStitches),
			maxOutputStitches: settings.maxOutputStitches,
			margin: 0,
		};
		return tileCombiner.compress(tiles, partialStitchConfig);
	}
	/**
	 * Cleans up all resources and removes references for garbage collection
	 * @param opts Destroy options controlling how child objects are destroyed
	 */
	public destroy(opts?: IDestroyOptions): void {
		this._shutdownSubject$.next();
		this._shutdownSubject$.complete();
		this._lightbox.destroy(opts);
		this._components!.destroy(opts);
		this._lightboxMask.destroy(opts);
		this._exportCapture!.purge();
		this._exportCapture = null;
		this._canvasAngularLand = null;
		this._application = null;
		this._components = null;
	}
	/**
	 * Updates the print preview display based on the provided configuration
	 * @param config The print configuration or null to hide the preview
	 */
	private update(config: IPrintConfiguration | null): void {
		if (!config) {
			this.reset();
			return;
		}
		this._lastConfig = config;
		this.show();
		this.updateLightbox(config);
		this.updateText(config.textSize);
		this._application?.markAsDirty();
	}
	/**
	 * Updates the lightbox dimensions and mask based on the current print configuration
	 * @param config The print configuration containing paper size and orientation
	 */
	private updateLightbox(config: IPrintConfiguration): void {
		const app: AbstractApplication = this._application as AbstractApplication;
		const camera: Camera = app.camera;
		this._lightbox.setSize(camera.view.width, camera.view.height);
		this._lightbox.alpha = settings.lightboxAlphs;
		this._lightbox.tint = settings.lightboxColor;

		this._viewport = this.calculateLightboxDimensions(config);

		// TP-7456
		// Clamp viewport to ensure it doesn't extend beyond camera bounds
		const maxX: number = Math.max(0, camera.view.width - this._viewport.width);
		const maxY: number = Math.max(0, camera.view.height - this._viewport.height);
		this._viewport.x = Math.min(this._viewport.x, maxX);
		this._viewport.y = Math.min(this._viewport.y, maxY);
		this._viewport.width = Math.min(this._viewport.width, camera.view.width - this._viewport.x);
		this._viewport.height = Math.min(this._viewport.height, camera.view.height - this._viewport.y);

		const worldX: number = camera.screenToRealX(this._viewport.x);
		const worldY: number = camera.screenToRealY(this._viewport.y);
		const worldWidth: number = this._viewport.width / camera.scale.x;
		const worldHeight: number = this._viewport.height / camera.scale.y;
		this._lightbox.setWorldSize(new Rectangle(worldX, worldY, worldWidth, worldHeight));
		this._lightboxMask.drawMask(
			this._viewport.x,
			this._viewport.y,
			this._viewport.width,
			this._viewport.height,
			camera.view.width,
			camera.view.height
		);
	}

	/**
	 * Calculate lightbox dimensions based on paper size aspect ratio and screen constraints
	 * @param config The current print configuration
	 * @returns Calculated lightbox dimensions and position
	 */
	private calculateLightboxDimensions(config: IPrintConfiguration): Rectangle {
		const app: AbstractApplication = this._application as AbstractApplication;
		const camera: Camera = app.camera;

		let paperWidth: number = config.width;
		let paperHeight: number = config.height;

		let displayWidth: number;
		let displayHeight: number;

		if (config.orientation === PaperOrientations.Portrait) {
			displayWidth = paperWidth;
			displayHeight = paperHeight;
		} else {
			displayWidth = paperHeight;
			displayHeight = paperWidth;
		}

		const pixelsPerInch: number = config.units === PaperUnits.Inches ? settings.pixelsPerInch : 1;

		let lightboxWidth: number = displayWidth * pixelsPerInch;
		let lightboxHeight: number = displayHeight * pixelsPerInch;

		const availableWidth: number = camera.view.width - settings.leftOffset - settings.margin * 2;
		const availableHeight: number = camera.view.height - settings.margin * 2;

		const maxWidth: number = availableWidth * settings.maxScreenPercentage;
		const maxHeight: number = availableHeight * settings.maxScreenPercentage;

		if (lightboxWidth > maxWidth || lightboxHeight > maxHeight) {
			const scaleFactorForWidth: number = maxWidth / lightboxWidth;
			const scaleFactorForHeight: number = maxHeight / lightboxHeight;
			const scaleFactor: number = Math.min(scaleFactorForWidth, scaleFactorForHeight);

			lightboxWidth *= scaleFactor;
			lightboxHeight *= scaleFactor;
		}

		if (lightboxWidth < settings.minSize) {
			const scaleUp: number = settings.minSize / lightboxWidth;
			lightboxWidth *= scaleUp;
			lightboxHeight *= scaleUp;
		}

		const availableAreaStartX: number = settings.leftOffset;
		const lightboxX: number = availableAreaStartX + (availableWidth - lightboxWidth) / 2 + settings.margin;
		// Set the Y to the header height so that the lightbox always includes the timeline elements in the preview
		// if don't want this, uncomment the line below:
		// const lightboxY: number = (camera.view.height - lightboxHeight) / 2;
		const lightboxY: number = cssVar.headerHeight_px;
		return new Rectangle(lightboxX, lightboxY, lightboxWidth, lightboxHeight);
	}
	/**
	 * Hides the lightbox overlay
	 */
	private hide(): void {
		this._lightbox.visible = false;
	}
	/**
	 * Shows the lightbox overlay
	 */
	private show(): void {
		this._lightbox.visible = true;
	}

	/**
	 * Helper method which resets state to default. This is called once the print preview is closed
	 * @private
	 */
	private reset(): void {
		this.hide();
		this.dispatchPreviewClosed();
		this._lastConfig = null;
		this._application!.markAsDirty();
		this._textSizeController!.reset();
	}

	/**
	 * Helper method to perform specific actions when the Lightbox is first opened.
	 * @private
	 */
	private onFirstOpen(): void {
		this.dispatchPreviewStarted();
		this._textSizeController!.setup();
	}

	/**
	 * Update ticket text scale based on print UI configuration
	 * @param config Print configuration
	 * @private
	 */
	private updateText(textSize: PrintTextSizes): void {
		switch (textSize) {
			case PrintTextSizes.Large:
				this._textSizeController!.setLarge();
				break;
			case PrintTextSizes.Small:
				this._textSizeController!.setSmall();
				break;
			default:
				this._textSizeController!.setNormal();
				break;
		}
	}

	/**
	 * Sets up the display layers for the lightbox and mask
	 */
	private setupLayers(): void {
		this._application?.layerManager.addLayer(LayerPaths.BLOCKER_PRINT_EXPORT);
		this._application?.layerManager.addChildToLayer(LayerPaths.BLOCKER_PRINT_EXPORT, this._lightbox);
		this._application?.layerManager.addChildToLayer(LayerPaths.BLOCKER_PRINT_EXPORT, this._lightboxMask);
		this._lightbox.setMask(this._lightboxMask);
	}
	/**
	 * Sets up subscriptions to print service observables for configuration changes and print actions
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand?.planPrintService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((config: IPrintConfiguration | null) => {
				// Reset state, do not update. Doing this because planPrintService.changed$ is spammy and is emitting a lot more
				// than expected. So we terminate the code flow to prevent needless updates.
				if (!config) {
					this.reset();
					return;
				}

				// Print preview was opened for first time, so we update any components that should run during the `Edit` phase
				if (!this._lastConfig && config) {
					this.onFirstOpen();
				}

				// Otherwise, normal update
				this.update(config);
			});

		this._canvasAngularLand?.planPrintService.printStatusUpdate$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((action: IPrintStatus) => {
				if (action.status === PrintActions.CaptureSingle || action.status === PrintActions.CaptureStitch) {
					this.capture();
					return;
				}
			});
	}
	/**
	 * Registers export components that modify behavior during the capture process
	 */
	private setupComponents(): void {
		// Functionality components
		this._components!.add(`hideTimeline`, new HideTimeline());
		this._components!.add(`hideTodayLine`, new HideTodayLine());
		this._components!.add(`hidePromisePeriod`, new HidePromisePeriod());
		this._components!.add(`drawWhiteBackground`, new DrawWhiteBackground());
		this._components!.add(`handleTicketInteraction`, new HandleTicketInteraction());
		this._components!.add(`hideSwimlaneLabel`, new HideSwimlaneLabel());
		this._components!.add(`hideMilestoneLabel`, new HideMilestoneLabel());
		this._components!.add(`hideActiveLineLabel`, new HideActiveLineLabel());
		this._components!.add(`crewSizeCapturer`, new CrewSizeCapturer());

		// Special drawing components (elements to include in the print
		this._components!.add(`crewSizeDrawer`, new CrewSizeDrawer());
		this._components!.add(`roleLegendDrawer`, new RoleLegendDrawer());
		this._components!.add(`projectAndPlanLabelDrawer`, new ProjectAndPlanLabelDrawer());
	}
}
